export type SelectOption = { value: number; label: string };
export type FormSelectOption = { value: number | string; label: string };

export function isValidSelectOptionValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return false;
  const num = Number(value);
  return Number.isFinite(num) && num > 0;
}

export function isValidSelectOptionLabel(label: unknown): boolean {
  if (typeof label !== "string") return false;
  const trimmed = label.trim();
  return trimmed.length > 0 && trimmed !== "-" && trimmed !== "—";
}

/**
 * Maps API rows or pre-built { value, label } pairs into deduplicated select options.
 * Drops invalid IDs, empty labels, and dash placeholders.
 */
export function normalizeSelectOptions<T>(
  items: T[],
  getLabel?: (item: T) => string | null | undefined
): SelectOption[] {
  const seen = new Set<number>();
  const result: SelectOption[] = [];

  for (const item of items) {
    let value: unknown;
    let label: unknown;

    if (
      item !== null &&
      typeof item === "object" &&
      "value" in item &&
      "label" in item
    ) {
      const opt = item as { value: unknown; label: unknown };
      value = opt.value;
      label = opt.label;
    } else if (getLabel) {
      const record = item as Record<string, unknown>;
      value = record.id ?? record.value;
      label = getLabel(item) ?? record.name ?? record.title ?? record.label;
    } else {
      const record = item as Record<string, unknown>;
      value = record.id ?? record.value;
      label = record.name ?? record.title ?? record.label;
    }

    if (!isValidSelectOptionValue(value)) continue;
    if (!isValidSelectOptionLabel(label)) continue;

    const id = Number(value);
    if (seen.has(id)) continue;
    seen.add(id);

    result.push({ value: id, label: String(label).trim() });
  }

  return result;
}

function coerceFormSelectValue(value: unknown): number | string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const num = Number(trimmed);
    if (Number.isFinite(num) && trimmed === String(num)) return num;
    return trimmed;
  }
  return null;
}

export function isValidFormSelectOptionValue(value: unknown): boolean {
  const coerced = coerceFormSelectValue(value);
  if (coerced === null) return false;
  if (typeof coerced === "number") return coerced > 0;
  return coerced.length > 0;
}

/**
 * Like normalizeSelectOptions but keeps non-numeric string values (e.g. gender: male/female).
 */
export function normalizeFormSelectOptions(
  items: { value: unknown; label: unknown }[]
): FormSelectOption[] {
  const seen = new Set<string>();
  const result: FormSelectOption[] = [];

  for (const item of items) {
    const coerced = coerceFormSelectValue(item.value);
    if (coerced === null || !isValidFormSelectOptionValue(coerced)) continue;
    if (!isValidSelectOptionLabel(item.label)) continue;

    const key = String(coerced);
    if (seen.has(key)) continue;
    seen.add(key);

    result.push({ value: coerced, label: String(item.label).trim() });
  }

  return result;
}
